"use client";

import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { ArrowUpRight } from "lucide-react";

import { Button } from "@/components/atoms/Button";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn, formatSectionTitle } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";

interface SalaryItem {
  meta_id?: number;
  id?: string;
  meta_salary_package: string;
  meta_package_description?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface AcademicDeliverables1001Data {
  title?: string;
  title_text_color?: string;
  sub_title?: string;
  sub_title_text_color?: string;
  section_background_color?: string;
  /** Overrides the theme's `--color-primary` for `{…}` spans and stat figures; empty = theme default */
  highlight_color?: string;
  recruiters_title?: string;
  recruiters_sub_title?: string;
  button_text?: string | null;
  desktop_bg_image?: string;
  mobile_bg_image?: string;
  alt_image?: string;
  metadata?: SalaryItem[];
  /** MySQL booleans may arrive as 0/1 */
  status?: boolean | number;
}

// Academic Deliverables 1001 — "Placement Success": a static stats band
// (title + sub-title + salary figures) over a soft background, followed by a
// white hiring-partners band (heading + sub-title + lead CTA + logo strip).
// Ported from the Chandigarh portal's PlacementLP organism.
const AcademicDeliverables1001 = ({ data }: { data: any }) => {
  const dispatch = useDispatch();
  const elementRef = useRef<HTMLElement>(null);
  const [isCtaHovered, setIsCtaHovered] = useState(false);

  // Handle both single and double nesting from PreviewRenderer
  const sectionData: AcademicDeliverables1001Data = data?.data ?? data ?? {};

  const {
    title = "",
    title_text_color = "#000000",
    sub_title = "",
    sub_title_text_color = "#000000",
    section_background_color = "#FFDBD9",
    highlight_color = "",
    recruiters_title = "",
    recruiters_sub_title = "",
    button_text,
    desktop_bg_image = "",
    mobile_bg_image = "",
    alt_image = "",
    metadata = [],
  } = sectionData;

  // Active salary rows, sorted by priority (mirrors AcademicDeliverables2).
  const salary = useMemo(() => {
    if (!metadata || metadata.length === 0) return [];
    return [...metadata]
      .filter((item) => item.meta_status)
      .sort((a, b) => (a.meta_priority ?? 0) - (b.meta_priority ?? 0));
  }, [metadata]);

  useEffect(() => {
    if (elementRef.current) {
      FireWidgetViewedEvent(elementRef, "academic_deliverables_section");
    }
  }, []);

  const active = sectionData.status === true || sectionData.status === 1;
  if (!active) {
    return null;
  }

  const btnText = button_text ?? "";
  const hasPartnersBand = Boolean(desktop_bg_image || mobile_bg_image);

  // Scope the theme's --color-primary to the configured highlight color so the
  // `{...}` spans (text-primary) and the stat figures pick it up; when empty,
  // the theme default applies (mirrors AcademicDeliverables2's heading trick).
  const highlightStyle = highlight_color
    ? ({ ["--color-primary" as string]: highlight_color } as React.CSSProperties)
    : undefined;

  return (
    <section ref={elementRef} style={{ fontFamily: "Poppins, sans-serif" }}>
      {/* Stats band */}
      <div
        className="flex flex-col items-center justify-center w-full mx-auto py-[50px] min-[768px]:py-[70px] px-5 min-[1024px]:px-[122px]"
        style={{ backgroundColor: section_background_color }}
      >
        <div className="max-w-[1200px] w-full flex flex-col items-center justify-center mx-auto">
          {title && (
            <h2
              className="text-[28px] max-w-[320px] min-[768px]:max-w-[500px] min-[1024px]:max-w-full min-[768px]:text-[36px] text-center font-semibold leading-[126%] tracking-[-0.816px] mb-[16px]"
              style={{ color: title_text_color, ...highlightStyle }}
              dangerouslySetInnerHTML={{
                __html: formatSectionTitle(title, "text-primary"),
              }}
            />
          )}

          {sub_title && (
            <p
              className="text-[14px] font-normal leading-[22px] text-center max-w-[302px] min-[768px]:max-w-[680px] mx-auto mb-8 min-[768px]:mb-[66px] min-[768px]:text-base min-[768px]:leading-relaxed"
              style={{ color: sub_title_text_color }}
            >
              {sub_title}
            </p>
          )}

          {salary.length > 0 && (
            <div
              className="flex flex-col min-[768px]:flex-row justify-around items-center gap-[60px] min-[768px]:gap-[20px] min-[1024px]:gap-[60px] min-[768px]:w-[780px]"
              style={highlightStyle}
            >
              {salary.map((item, index) => (
                <div
                  key={item.meta_id ?? item.id ?? `salary-${index}`}
                  className="flex flex-col min-[768px]:w-[220px] items-center"
                >
                  <div className="text-[44px] font-bold text-primary">
                    {item.meta_salary_package}
                  </div>
                  <div className="text-[14px] leading-[18px] font-normal text-center">
                    {item.meta_package_description}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Hiring-partners band */}
      {hasPartnersBand && (
        <div className="bg-[#FFFFFF] flex flex-col items-center justify-center w-full mx-auto py-6 min-[768px]:py-[38px]">
          <div className="w-full">
            <div className="text-center flex flex-col items-center justify-center gap-[16px] py-[16px] px-5">
              {recruiters_title && (
                <h2
                  className="text-[28px] min-[1024px]:text-[32px] text-[#000000] text-center font-semibold leading-[126%] tracking-[-0.816px]"
                  style={highlightStyle}
                  dangerouslySetInnerHTML={{
                    __html: formatSectionTitle(recruiters_title, "text-primary"),
                  }}
                />
              )}

              {recruiters_sub_title && (
                <p className="text-[14px] text-[#000000] font-normal leading-[22px] text-center max-w-[302px] min-[768px]:max-w-[680px] mx-auto">
                  {recruiters_sub_title}
                </p>
              )}

              {btnText && (
                <Button
                  classType="primary"
                  className="px-[18px] py-3 gap-2.5"
                  onClick={() =>
                    dispatch(
                      handleOpenLeaddrop({
                        variant: LEAD_DROP_VARIANTS.REGISTER,
                      })
                    )
                  }
                  onMouseEnter={() => setIsCtaHovered(true)}
                  onMouseLeave={() => setIsCtaHovered(false)}
                >
                  {btnText}
                  <span className="size-6">
                    <ArrowUpRight
                      className={cn(
                        "transition-transform duration-300",
                        isCtaHovered ? "rotate-[45deg]" : "rotate-0"
                      )}
                    />
                  </span>
                </Button>
              )}
            </div>

            <div className="w-full mb-6">
              <div className="w-full mx-auto">
                {desktop_bg_image && (
                  <div className="hidden min-[768px]:block">
                    <Image
                      src={desktop_bg_image}
                      alt={alt_image || "Hiring Partner Companies"}
                      width={1120}
                      height={288}
                      className="object-contain w-full"
                    />
                  </div>
                )}

                {(mobile_bg_image || desktop_bg_image) && (
                  <div className="block min-[768px]:hidden w-full">
                    <Image
                      src={mobile_bg_image || desktop_bg_image}
                      alt={alt_image || "Hiring Partner Companies"}
                      width={375}
                      height={400}
                      className="w-full h-auto object-contain"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AcademicDeliverables1001;
